import { useState, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Pencil, Infinity, Timer, BookOpenText, Settings, Info, MoreHorizontal, GraduationCap, House, Puzzle, LogIn, LogOut, Trophy, Users, Blend, Award } from 'lucide-react';
import DiscordIcon from './DiscordIcon.tsx';
import MoreSheet from './MoreSheet';
import { useAuth } from '../hooks/useAuth.ts';
import { useDiscordJoin } from '../hooks/useDiscordJoin.ts';
import { useProfile } from '../hooks/useProfile.ts';
import Avatar from './Avatar.tsx';
import { useNotifications } from '../context/NotificationContext.tsx';

// ── Types ────────────────────────────────────────────────────────────────────

interface SubItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  matchPrefixes?: string[];
  href?: string;
}

interface LeftNavProps {
  onOpenSettings: () => void;
}

// ── NavItem ──────────────────────────────────────────────────────────────────

function NavItem({ label, icon, active, onClick, indent, primary, chevron, className, showPulse }: {
  label: string; icon: React.ReactNode; active: boolean; onClick: () => void;
  indent?: boolean; primary?: boolean; chevron?: 'open' | 'closed'; className?: string;
  showPulse?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      data-nav-primary={primary ? 'true' : undefined}
      className={`nav-item${active ? ' nav-active' : ''}${className ? ` ${className}` : ''}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%',
        padding: indent ? '7px 14px 7px 38px' : '9px 14px',
        border: 'none',
        cursor: 'pointer',
        color: active ? 'var(--accent)' : 'var(--ink-soft)',
        fontFamily: 'var(--font-ui)',
        fontSize: indent ? 13 : 14,
        fontWeight: active ? 600 : 500,
        borderRadius: 'var(--radius-md)',
        textAlign: 'left',
        position: 'relative',
        transition: 'background .15s, color .15s',
      }}
    >
      <span style={{ width: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: active ? 1 : 0.7, fontSize: 14, position: 'relative' }}>
        {icon}
        {showPulse && <span className="nav-pulse-dot" aria-hidden="true" />}
      </span>
      <span style={{ flex: chevron ? 1 : undefined }}>{label}</span>
      {chevron && (
        <span style={{
          color: 'var(--muted)', transition: 'transform .2s',
          transform: chevron === 'open' ? 'rotate(0)' : 'rotate(-90deg)', fontSize: 20,
        }}>▾</span>
      )}
    </button>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const PUZZLE_SUB_ITEMS: SubItem[] = [
  { label: 'Browse', path: '/puzzles/browse', icon: <Search size={13} strokeWidth={1.8} style={{ flexShrink: 0 }} />, matchPrefixes: ['/puzzles/browse', '/puzzles/solve'] },
  { label: 'Endless', path: '/puzzles/endless', icon: <Infinity size={13} strokeWidth={1.6} style={{ flexShrink: 0 }} /> },
  { label: 'Speedrun', path: '/puzzles/speedrun', icon: <Timer size={13} strokeWidth={1.6} style={{ flexShrink: 0 }} />, matchPrefixes: ['/puzzles/speedrun'] },
  { label: 'Editor', path: '/puzzles/editor', icon: <Pencil size={13} strokeWidth={1.8} style={{ flexShrink: 0 }} /> },
];

const LESSON_SUB_ITEMS: SubItem[] = [
  { label: 'How to play', path: '/lessons/how-to-play', icon: <BookOpenText size={13} strokeWidth={1.8} style={{ flexShrink: 0 }} /> },
];

const COMMUNITY_SUB_ITEMS: SubItem[] = [
  { label: 'Leaderboard', path: '/community/leaderboard', icon: <Trophy size={13} strokeWidth={1.8} style={{ flexShrink: 0 }} />, matchPrefixes: ['/community/leaderboard'] },
  { label: 'Discord', path: '/community/discord', icon: <DiscordIcon size={13} />, href: 'https://discord.gg/MuCFu8wnfv' },
];

export default function LeftNav({ onOpenSettings }: LeftNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, login, logout } = useAuth();
  const handleDiscordJoin = useDiscordJoin();
  const { username: appUsername, avatarFilename: appAvatarFilename } = useProfile();
  const { activeRoomCount } = useNotifications();
  const [moreOpen, setMoreOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const [bar, setBar] = useState<{ top: number; height: number; left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const active = nav.querySelector('button.nav-active') as HTMLElement | null;
    if (active) {
      const navRect = nav.getBoundingClientRect();
      const itemRect = active.getBoundingClientRect();
      setBar({
        top: itemRect.top - navRect.top,
        height: itemRect.height,
        left: itemRect.left - navRect.left,
        width: itemRect.width,
      });
    }
  }, [location.pathname, isLoading, isAuthenticated]);
  const puzzlesOpen = location.pathname.startsWith('/puzzles');
  const communityOpen = location.pathname.startsWith('/community');
  const lessonsOpen = location.pathname.startsWith('/lessons');
  const multiplayerActive = location.pathname.startsWith('/multiplayer');

  const isHome = location.pathname === '/';
  const isAbout = location.pathname === '/about';

  return (
    <>
      <nav
        ref={navRef}
        className="left-nav"
        style={{
          width: 240,
          height: '100vh',
          background: 'var(--surface)',
          borderRight: '1px solid var(--line)',
          padding: '20px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          flexShrink: 0,
          position: 'relative',
        }}
      >
        {bar !== null && (<>
          {/* Desktop: vertical left bar */}
          <div aria-hidden className="nav-bar-vertical" style={{
            position: 'absolute',
            left: 13,
            top: bar.top + 6,
            height: bar.height - 12,
            width: 2,
            borderRadius: 2,
            background: 'var(--accent)',
            transition: 'top 0.28s cubic-bezier(0.4, 0, 0.2, 1), height 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'none',
          }} />
          {/* Mobile: horizontal top bar */}
          <div aria-hidden className="nav-bar-horizontal" style={{
            position: 'absolute',
            top: 0,
            left: bar.left,
            width: bar.width,
            height: 2,
            borderRadius: 2,
            background: 'var(--accent)',
            transition: 'left 0.28s cubic-bezier(0.4, 0, 0.2, 1), width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'none',
          }} />
        </>)}
        {/* Logo */}
        <button
          className="left-nav-logo"
          onClick={() => navigate('/')}
          style={{
            display: 'flex', alignItems: 'center', gap: 2,
            padding: '6px 10px 18px',
            background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
          }}
        >
          <img
            src="/logo/logo-light.svg"
            alt="Rikoro logo"
            className="logo-light"
            style={{ width: 48, height: 48 }}
          />
          <img
            src="/logo/logo-dark.svg"
            alt="Rikoro logo"
            className="logo-dark"
            style={{ width: 48, height: 48 }}
          />
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 500,
            lineHeight: 1, color: 'var(--ink)', paddingTop: 1
          }}>RIKORO</div>
        </button>

        {/* Home */}
        <NavItem label="Home" icon={<House/>} active={isHome} onClick={() => navigate('/')} primary />

        {/* Puzzles section */}
        <NavItem label="Puzzles" icon={<Puzzle />} active={puzzlesOpen} onClick={() => navigate('/puzzles')} primary chevron={puzzlesOpen ? 'open' : 'closed'} />

        {puzzlesOpen && PUZZLE_SUB_ITEMS.map((sub) => {
          const subActive = sub.matchPrefixes
            ? sub.matchPrefixes.some((p) => location.pathname.startsWith(p))
            : location.pathname.startsWith(sub.path);
          return (
            <NavItem
              key={sub.path}
              indent
              label={sub.label}
              icon={sub.icon}
              active={subActive}
              onClick={() => navigate(sub.path)}
            />
          );
        }        )}

        {/* Multiplayer */}
        <NavItem
          label="Multiplayer"
          icon={<Blend />}
          active={multiplayerActive}
          onClick={() => navigate('/multiplayer')}
          primary
          showPulse={activeRoomCount > 0 && !multiplayerActive}
        />

        {/* Community section */}
        <NavItem label="Community" icon={<Users />} active={communityOpen} onClick={() => navigate('/community')} chevron={communityOpen ? 'open' : 'closed'} />

        {communityOpen && COMMUNITY_SUB_ITEMS.map((sub) => {
          const subActive = sub.href ? false : sub.matchPrefixes
            ? sub.matchPrefixes.some((p) => location.pathname.startsWith(p))
            : location.pathname.startsWith(sub.path);
          return (
            <NavItem
              key={sub.path}
              indent
              label={sub.label}
              icon={sub.icon}
              active={subActive}
              onClick={() => sub.href ? handleDiscordJoin() : navigate(sub.path)}
            />
          );
        })}

        {/* Achievements — desktop only (accessible via More sheet on mobile) */}
        <NavItem
          label="Achievements"
          icon={<Award />}
          active={location.pathname === '/achievements'}
          onClick={() => navigate('/achievements')}
          primary
          className="left-nav-achievements-desktop"
        />

        {/* Lessons section */}
        <NavItem label="Lessons" icon={<GraduationCap />} active={lessonsOpen} onClick={() => navigate('/lessons')} chevron={lessonsOpen ? 'open' : 'closed'} />

        {lessonsOpen && LESSON_SUB_ITEMS.map((sub) => {
          const subActive = sub.matchPrefixes
            ? sub.matchPrefixes.some((p) => location.pathname.startsWith(p))
            : location.pathname.startsWith(sub.path);
          return (
            <NavItem
              key={sub.path}
              indent
              label={sub.label}
              icon={sub.icon}
              active={subActive}
              onClick={() => navigate(sub.path)}
            />
          );
        })}

        {/* Spacer */}
        <div className="left-nav-spacer" style={{ flex: 1 }} />

        {/* Profile / Log in */}
        {!isLoading && (
          isAuthenticated ? (
            <>
              <NavItem
                label="Profile"
                icon={<Avatar username={appUsername ?? ''} avatarFilename={appAvatarFilename} size={24} />}
                active={location.pathname === '/profile' || location.pathname.startsWith('/profile/')}
                onClick={() => navigate('/profile')}
                primary
              />
              <NavItem
                label="Log out"
                icon={<LogOut size={16} strokeWidth={1.5} />}
                active={false}
                onClick={logout}
              />
            </>
          ) : (
            <NavItem
              label="Log in"
              icon={<LogIn />}
              active={false}
              onClick={login}
              primary
            />
          )
        )}

        {/* About */}
        <NavItem
          label="About"
          icon={<Info />}
          active={isAbout}
          onClick={() => navigate('/about')}
        />

        {/* Settings — desktop only (hidden on mobile, replaced by More tab) */}
        <NavItem
          label="Settings"
          icon={<Settings />}
          active={false}
          onClick={onOpenSettings}
          className="left-nav-settings-desktop"
        />

        {/* More — mobile only (hidden on desktop) */}
        <NavItem
          label="More"
          icon={<MoreHorizontal />}
          active={moreOpen}
          onClick={() => setMoreOpen(true)}
          primary
          className="left-nav-more"
        />

      </nav>

      {moreOpen && (
        <MoreSheet
          onClose={() => setMoreOpen(false)}
          onOpenSettings={onOpenSettings}
          onNavigate={(path) => navigate(path)}
        />
      )}
    </>
  );
}
